import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { sortBy, uniq } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';

import type BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type SourceType from 'webapp/models-custom/source-type';

export const viewSettingsNamePrefix = 'popupSelectDataboardsFromAnotherSpaceViewSettings';
export const filterSettingsNamePrefix = 'popupSelectDataboardsFromAnotherSpaceFilterSettings';

export default class FilterSortBoardFromAnotherSpaceAtSelectPopup extends FilterSort<BoardFromAnotherSpace> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked override sortProp = 'name';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override searchProps: SearchProps = {
		name: 1,
	};

	// Use this only to save selected filters in modal until its destroyed (resetFilterSettings used in boards/popup-select to reset filters)
	// Case: Use selected filters when switching between views grid/table then reset it for new modal "session".
	override settingsNamePrefix = filterSettingsNamePrefix;

	constructor(options: FilterSortSettings<BoardFromAnotherSpace>) {
		super(options);
		const selectedFilters = this.readSavedSelectedFilters();

		this.createFilter<number>({
			id: 1,
			selected: selectedFilters[1] as number | undefined,
			label: 'Account',
			name: 'account',
			isMultiSelect: true,
			getOptions: () => {
				return this.accountsOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((spaceId) => {
					return item.spaceId === spaceId;
				});
			},
		});

		this.createFilter<string>({
			id: 2,
			selected: selectedFilters[2] as string | undefined,
			label: 'Data source',
			name: 'data source',
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceTypeKey) => {
					return !!item.sourceTypes.find(
						(sourceType: SourceType) => sourceType.key === sourceTypeKey,
					);
				});
			},
		});
	}

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
		];
	}

	@cached
	get accountsOptions() {
		const list: FilterOption<number>[] = [];

		const allSpaceIds = uniq(this.allItems.map((i) => i.spaceId)),
			agencySpaceRecord = this.mmSpacesService.agencySpaceRecord,
			clientSpaceRecords = sortBy(this.mmSpacesService.otherSpaceRecords, 'companyName');

		const agencySpaceId = agencySpaceRecord ? +agencySpaceRecord.id! : undefined;
		if (agencySpaceRecord && agencySpaceId && allSpaceIds.includes(agencySpaceId)) {
			list.push({
				id: agencySpaceId,
				text: `${agencySpaceRecord.companyName} (organization)`,
			});
		}
		clientSpaceRecords.forEach((clientSpaceRecord) => {
			const clientSpaceId = +clientSpaceRecord.id!;
			if (allSpaceIds.includes(clientSpaceId)) {
				list.push({
					id: clientSpaceId,
					text: clientSpaceRecord.companyName,
				});
			}
		});

		list.unshift({ id: 0, text: 'All' });

		return list;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeIdsArray: string[] = [];

		this.allItems.forEach((item: BoardFromAnotherSpace) => {
			const sourceTypeIds = item.sourceTypes.map((sourceType) => sourceType.key);
			sourceTypeIdsArray.push(...uniq(sourceTypeIds));
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeIdsArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}
}
